import React, { useState } from 'react';
import { noop } from 'lodash';

import CardDialog from '~/components/CardDialog';
import FsButton from '~/components/core/FsWrappers/FsButton/FsButton';
import Text from '~/components/core/TextComponents/Text';
import FinancesTable from '~/components/Finances/FinancesTable';
import { COLUMN_KEYS, EXTENDABLE_COLUMN_KEYS } from '~/components/Finances/FinancesTable/constants';
import type { Finance } from '~/server_shared/swagger/data-contracts';

interface WarningInProgressPaymentsDialogProps {
  finances: Finance[];
  onCancel: () => void;
  onProceed: () => void;
  disabled?: boolean;
}

// Columns to display in warning dialog (subset of payment search columns)
const WARNING_DIALOG_COLUMNS = [
  COLUMN_KEYS.claim_id_display, // Claim link
  COLUMN_KEYS.financial_status, // Status chip
  COLUMN_KEYS.amount, // Amount
  COLUMN_KEYS.payable_with_reserve_type, // Indemnity/Expenses
  EXTENDABLE_COLUMN_KEYS.issued_by, // Requester
  EXTENDABLE_COLUMN_KEYS.sent_to, // Payee name
  EXTENDABLE_COLUMN_KEYS.payment_method, // Bank transfer method
  EXTENDABLE_COLUMN_KEYS.vat_details, // VAT/GST included amount or "Not included"
];

const WarningInProgressPaymentsDialog: React.FC<WarningInProgressPaymentsDialogProps> = ({
  finances,
  onCancel,
  onProceed,
  disabled,
}) => {
  const [sortByColumn, setSortByColumn] = useState({ id: 'request_date', order: 'desc' as const });

  return (
    <CardDialog isDialog title="Update Bank Details?" maxWidth="lg" fullWidth>
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="mb-12">
        This contact has {finances.length} payment{finances.length > 1 ? 's' : ''} in progress that use bank transfer
        methods:
      </Text>

      <div className="my-12">
        <FinancesTable
          finances={finances}
          displayColumnIds={WARNING_DIALOG_COLUMNS}
          sortByColumn={sortByColumn}
          onSortByColumn={setSortByColumn}
          onReloadFinances={noop}
        />
      </div>

      <Text
        variant={Text.VARIANTS.SM}
        weight={Text.WEIGHTS.REGULAR}
        colorVariant={Text.COLOR_VARIANTS.SECONDARY}
        className="mb-20 mt-12"
      >
        These payments are associated with the current bank details. The old values will remain on these payments -
        consider canceling and recreating them with the new details.
      </Text>

      <div className="mt-px flex w-full justify-end">
        <FsButton variant="contained" color="secondary" disabled={disabled} onClick={onCancel}>
          Cancel
        </FsButton>
        <FsButton className="ml-12" variant="contained" color="primary" disabled={disabled} onClick={onProceed}>
          Update Anyway
        </FsButton>
      </div>
    </CardDialog>
  );
};

export default WarningInProgressPaymentsDialog;
